import type { ClientRequirement } from "../widget/api/resident-agent-protocol";

export type { ClientRequirement };

// W3C Permissions Policy feature tokens used by this widget.
// Spec: https://w3c.github.io/webappsec-permissions-policy/
// Feature list: https://github.com/w3c/webappsec-permissions-policy/blob/main/features.md
export type MessageModality = "Text" | "Voice";

export const MessageSources = {
  QuickLink: "quick_link",
} as const;
export type MessageSource =
  (typeof MessageSources)[keyof typeof MessageSources];

export const PermissionPolicies = {
  ClipboardWrite: "clipboard-write",
  Microphone: "microphone",
  Autoplay: "autoplay",
} as const;
export type PermissionPolicy =
  (typeof PermissionPolicies)[keyof typeof PermissionPolicies];

export interface WidgetConfig {
  agentId: string;
  organizationId?: string;
  apiUrl: string;
  sourceUrl?: string;
  disableSendMessage?: boolean;
  openOnLoad?: boolean;
}

export interface QuickPromptConfig {
  agentId?: string;
  apiUrl?: string;
  prompt: string;
}

export interface AgentQuickPrompt {
  title: string;
  prompt: string;
}

export interface AgentConfig {
  organizationId: string;
  name: string;
  tone?: string;
  messageLength?: string;
  allowedOrigins?: Array<{ origin: string; hiddenPaths: string[] }>;
  quickPrompts?: AgentQuickPrompt[];
  iconUrl?: string | null;
  primaryColor?: string | null;
  agentTitle?: string;
  subtitle?: string;
  position?: "BottomRight" | "BottomLeft";
  subheading?: string | null;
  welcomeMessage?: string | null;
  footerText?: string | null;
  fabCallout?: string | null;
  fabSize?: FabSize;
  fabBottomOffset?: number;
  removeBackground?: boolean;
  permissionPolicies?: PermissionPolicy[];
  voiceEnabled?: boolean;
}

export enum FabSize {
  Small = "Small",
  Medium = "Medium",
  Large = "Large",
}

export type ErrorType =
  | "network"
  | "rateLimit"
  | "serverError"
  | "clientError"
  | "unknown";

export interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  contentFormat?: "markdown" | "html";
  timestamp: number;
  error?: boolean;
  errorType?: ErrorType;
  retryable?: boolean;
  originalUserMessage?: string;
}

export interface ConversationHandoff {
  conversationId: string;
  messages: Message[];
}

export interface PendingApproval {
  toolApprovalId: string;
  conversationId: string;
  toolCallId: string;
  toolName: string;
  args: Record<string, unknown>;
  clientRequirements?: ClientRequirement[];
}

export interface ConversationState {
  conversationId: string | null;
  messages: Message[];
  isLoading: boolean;
  error: string | null;
}
